import { apiPlugin, storyblokInit, type SbBlokData } from '@storyblok/react';

import { components } from './components';

// Fallback component for any blok whose `component` field has no entry in the
// registry. Logs a console.warn so unmapped bloks are visible in dev/CI logs
// (R2.2: "log a warning that names the unmapped component"); renders null so
// the rest of the page continues to render without crashing.
const UnmappedBlokFallback = ({ blok }: { blok?: SbBlokData }) => {
  if (blok) {
    console.warn(
      `Storyblok: no React component registered for blok "${blok.component}" (uid=${blok._uid})`
    );
  }
  return null;
};

let initialized = false;

// Idempotent bootstrap for the Storyblok React SDK.
//
// - Reads only GATSBY_-prefixed env vars (the only env vars Gatsby exposes
//   to client bundles). Any non-GATSBY_ Storyblok env var is server/build-only
//   and must never be referenced from this layer.
// - Bridge is enabled iff GATSBY_STORYBLOK_PREVIEW_TOKEN is present in the
//   environment. The presence of that var is the single signal for "this is a
//   draft-serving environment that runs the Visual Editor". In production, the
//   var is absent and the bridge JS does not load.
// - The api plugin is registered only when a token is present so a bridgeless
//   build does not pay for a misconfigured Storyblok client.
export function initStoryblok(): void {
  if (initialized) return;

  const token = process.env.GATSBY_STORYBLOK_PREVIEW_TOKEN;
  const region = process.env.GATSBY_STORYBLOK_REGION ?? 'eu';

  storyblokInit({
    accessToken: token,
    use: token ? [apiPlugin] : [],
    apiOptions: { region },
    bridge: Boolean(token),
    components,
    enableFallbackComponent: true,
    customFallbackComponent: UnmappedBlokFallback,
  });

  initialized = true;
}
